'use client';

import {useI18n} from '@udemy-v2/i18n';
import {Accordion, type AccordionRenderTitleProps} from '@udemy-v2/react-reveal-components';

import {QuickAnswerCard, type QuickAnswer} from './quick-answer-card.react-component';

import styles from './questions-and-answers.module.scss';

export interface FaqEntry {
    /** The question, shown on the accordion's toggle row. */
    question: string;
    /** The answer, revealed when the row is expanded. */
    answer: string;
}

export interface FaqGroup {
    /** Heading above the group's accordion, e.g. "About the Platform". */
    label: string;
    /** The group's question/answer pairs, in the order Figma lists them. */
    entries: FaqEntry[];
}

export interface QuestionsAndAnswersProps {
    /**
     * The four short answers shown as cards above the FAQ. Defaults to the ones drawn in
     * Figma, assembled inside the component so every string stays a literal `gettext(…)`.
     */
    quickAnswers?: QuickAnswer[];
    /**
     * The grouped FAQ list below the cards. Defaults to the questions drawn in Figma.
     */
    groups?: FaqGroup[];
}

/**
 * Renders each accordion's question into an `<h4>` instead of the design system's default
 * `<h3>`, which would tie with the group label above it. Defined at module scope so the
 * panels are not handed a new function on every render.
 */
function renderQuestionTitle({className, children}: AccordionRenderTitleProps) {
    return <h4 className={className}>{children}</h4>;
}

/**
 * Questions and answers — Figma node 5260:158102 (desktop) and 5260:161872 (mobile), file
 * `mitwWlKOuf1pyo4gUCLEBY`. The page's last content section.
 *
 * A heading and description over four "quick answer" cards, then the full FAQ as three
 * labelled groups of accordion rows. The accordion is the design system's
 * `Accordion`/`Accordion.Panel` from `@udemy-v2/react-reveal-components`, which Code
 * Connect maps this frame's `AccordionTile` to and whose borders, padding and 18px title
 * already match the design; the `Accordion` wrapper is required, since a bare panel has no
 * context to toggle against.
 *
 * Figma (node 6238:85286) lists all fifteen questions but only carries answer copy for
 * two of them. The remaining thirteen answers are written here, kept to the voice of the
 * two that came from design; they are placeholders for content to confirm, not policy —
 * the membership ones in particular (price, cancellation, access after cancelling) state
 * how it is expected to work and should be checked against the real terms.
 */
export function QuestionsAndAnswers({quickAnswers, groups}: QuestionsAndAnswersProps) {
    const {gettext} = useI18n();

    const cards =
        quickAnswers ??
        ([
            {
                question: gettext('What is an instructor community?'),
                // Figma prefixes this one answer with "A: ", which none of the other three
                // carry — an editing leftover rather than a pattern, so it is dropped.
                answer: gettext(
                    'It’s a dedicated space where you can connect directly with your instructor and fellow learners through live events, structured programming, and a community forum.',
                ),
            },
            {
                question: gettext('Is this the same as Udemy?'),
                answer: gettext(
                    'Not quite! Your community lives on a separate platform built specifically for deeper connection and collaboration — think of it as your instructor’s home base.',
                ),
            },
            {
                question: gettext('What’s included in my subscription?'),
                answer: gettext(
                    'You’ll get access to live events, bootcamps, homework, projects, tools & resources, and a community forum.',
                ),
            },
            {
                question: gettext('How do I join a community?'),
                answer: gettext(
                    'Choose the instructor you are interested in and in their Instructor Profile page click on the Subscribe button.',
                ),
            },
        ] satisfies QuickAnswer[]);

    const faqGroups =
        groups ??
        ([
            {
                label: gettext('About the Platform'),
                entries: [
                    {
                        question: gettext(
                            'Why is the community on a different platform than Udemy?',
                        ),
                        answer: gettext(
                            'The community platform is purpose-built for interaction and collaboration — things like live events, forums, and group projects need a dedicated space to work well. Think of Udemy as where you learn, and the community as where you connect.',
                        ),
                    },
                    {
                        question: gettext('Will I need a separate account?'),
                        answer: gettext(
                            'Yes — your Circle account gets you in. You’ll need an account for accessing the community',
                        ),
                    },
                    {
                        question: gettext('Is the community platform available on mobile?'),
                        answer: gettext(
                            'Yes — the community works in your phone’s browser, so you can follow along, post, and join events from anywhere.',
                        ),
                    },
                    {
                        question: gettext('Is my data safe on the community platform?'),
                        answer: gettext(
                            'Your information is handled according to the community platform’s own privacy policy, which you can review before you sign up. Only what you choose to share is visible to the instructor and other members.',
                        ),
                    },
                ],
            },
            {
                label: gettext('About membership'),
                entries: [
                    {
                        question: gettext('How much does it cost to join a community?'),
                        answer: gettext(
                            'Each instructor sets their own subscription price, so it varies from community to community. You’ll see the exact price on the instructor’s profile page before you subscribe.',
                        ),
                    },
                    {
                        question: gettext('Can I try a community before committing?'),
                        answer: gettext(
                            'Every instructor’s profile page shows what their community includes — the events, resources, and forum you’d be joining — so you can see what you’re getting before you subscribe.',
                        ),
                    },
                    {
                        question: gettext('Can I cancel my community membership?'),
                        answer: gettext(
                            'Yes — you can cancel your subscription at any time from your subscription settings. There’s no long-term commitment.',
                        ),
                    },
                    {
                        question: gettext('What happens to my access if I cancel?'),
                        answer: gettext(
                            'You’ll keep access to the community until the end of the period you’ve already paid for, and it ends after that. You can resubscribe later whenever you like.',
                        ),
                    },
                    {
                        question: gettext('Can I join a community without a Udemy course?'),
                        answer: gettext(
                            'Yes — a community subscription is separate from the instructor’s courses, so you don’t need to be enrolled in one to join.',
                        ),
                    },
                ],
            },
            {
                label: gettext('About the experience'),
                entries: [
                    {
                        question: gettext(
                            'What’s the difference between a community and a course?',
                        ),
                        answer: gettext(
                            'A course is structured content you work through on your own schedule. A community is the ongoing, live side of learning — events, discussion, and direct contact with your instructor and fellow learners.',
                        ),
                    },
                    {
                        question: gettext('How active are the communities?'),
                        answer: gettext(
                            'It depends on the instructor — each one sets their own rhythm of live events, bootcamps, and forum activity. The instructor’s profile page gives you a sense of what to expect.',
                        ),
                    },
                    {
                        question: gettext('Can I interact directly with the instructor?'),
                        answer: gettext(
                            'Yes — that’s the point of a community. You can reach your instructor through live events and the community forum rather than only through course Q&A.',
                        ),
                    },
                    {
                        question: gettext('What if I’m not sure which community to join?'),
                        answer: gettext(
                            'Browse the communities above and take a look at each instructor’s profile page to see what they offer. Start with the instructor whose teaching already works for you.',
                        ),
                    },
                    {
                        question: gettext('Is community content exclusive?'),
                        answer: gettext(
                            'Yes — live events, bootcamps, projects, and resources shared in a community are for its members and aren’t part of the instructor’s courses.',
                        ),
                    },
                    {
                        question: gettext('What languages are communities available in?'),
                        answer: gettext(
                            'Each instructor runs their community in the language they teach in, so it varies. Check the instructor’s profile page to see which language theirs is in.',
                        ),
                    },
                ],
            },
        ] satisfies FaqGroup[]);

    return (
        <section className={styles['faq']} data-purpose="questions-and-answers">
            <div className={styles['inner']}>
                <div className={styles['intro']}>
                    <h2 className={styles['heading']}>
                        {gettext('Got questions? We’ve got answers.')}
                    </h2>
                    <p className={styles['description']}>
                        {gettext(
                            'Everything you need to know about instructor communities on Udemy.',
                        )}
                    </p>
                    <ul className={styles['cards']}>
                        {cards.map((card) => (
                            <QuickAnswerCard key={card.question} {...card} />
                        ))}
                    </ul>
                </div>

                <div className={styles['groups']}>
                    {faqGroups.map((group) => (
                        <div key={group.label} className={styles['group']}>
                            <h3 className={styles['group-label']}>{group.label}</h3>
                            {/* `toggleBehavior="any"` matches the design, which draws two
                                panels of this group open at once. */}
                            <Accordion
                                className={styles['panels']}
                                size="large"
                                toggleBehavior="any"
                            >
                                {group.entries.map((entry) => (
                                    <Accordion.Panel
                                        key={entry.question}
                                        title={entry.question}
                                        renderTitle={renderQuestionTitle}
                                    >
                                        {/* An unwritten answer opens onto nothing
                                            rather than an empty paragraph's leading. */}
                                        {entry.answer ? (
                                            <p className={styles['answer']}>{entry.answer}</p>
                                        ) : null}
                                    </Accordion.Panel>
                                ))}
                            </Accordion>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
